/**
 * Pós-processamento (F52), a parte pura: o plano por preset e opções, o estilo (números do dono) e o
 * espelho em TypeScript da correção de cor e da vinheta, que os testes comparam com os shaders.
 */

/** Tier do pós: o preset efetivo das Opções (no Automático, o medido). */
export type PostTier = "baixo" | "medio" | "alto";

/**
 * As opções de Gráficos do pós ("Brilho (bloom)", "Correção de cor", "Vinheta"). O "Contorno" (F52) e o
 * "Desfoque de distância" (Fase 13) saíram na Fase 15.
 */
export interface PostToggles {
  bloom: boolean;
  grade: boolean;
  vignette: boolean;
  /**
   * Fase 13 (G5): "Luz cinematográfica" — oclusão de ambiente (SSAO) e raios do sol no Médio e no Alto;
   * no Alto, também o reflexo em tela (SSR) e a luz indireta (GI).
   */
  light: boolean;
  /** "Aberração cromática" (sutil, nas bordas da tela). */
  aberration: boolean;
  /** "Reflexo de lente" (fantasmas e risco do sol); "Reduzir lampejos" apaga. */
  flare: boolean;
  /** Fase 14: "Desfoque de movimento" (a Investida, o Salto e o golpe forte); "Reduzir lampejos" apaga. */
  motion: boolean;
}

/**
 * O alvo em meio-float pode receber desenho (Gate D): no WebGL2 isso vem com `EXT_color_buffer_float`
 * ou, nos aparelhos que só têm o meio-float, com `EXT_color_buffer_half_float`. Sem nenhum, o compositor
 * cai para 8 bits sem bloom.
 */
export function halfFloatTargets(extensions: { has(name: string): boolean }): boolean {
  return extensions.has("EXT_color_buffer_float") || extensions.has("EXT_color_buffer_half_float");
}

/** O que o navegador oferece: alvo em meio-float (`halfFloatTargets`). */
export interface PostCaps {
  float: boolean;
}

/** O que vale num quadro: o tier, as opções e "Reduzir lampejos" (lidos a cada quadro de `QUALITY.post`). */
export interface PostSettings extends PostToggles {
  tier: PostTier;
  reduceFlashes: boolean;
}

/**
 * O que o quadro desenha. Sem `composer`, a cena vai direto ao canvas (o Baixo, como antes da F52).
 * A correção de cor fica sempre no passe quando há compositor (`grade` decide se ela corrige ou passa
 * a cor igual): ela devolve o alfa 1 (o WebKit mostra o alfa do canvas) e, com o bloom, traz o que
 * passou de 1 de volta ao matiz de antes.
 */
export interface PostPlan extends PostToggles {
  tier: PostTier;
  composer: boolean;
  /** Buffers do compositor em meio-float; sem a extensão, 8 bits sRGB (e sem bloom). */
  float: boolean;
  /** Níveis do borrão do bloom (F61: 3 no Médio, 5 no Alto; 0 sem bloom). */
  levels: number;
  /** A onda de choque (F94): só no Alto, com compositor. O Médio fica com o passe de antes. */
  onda: boolean;
  /** Fase 13: SSAO (Médio e Alto, com "Luz cinematográfica"). */
  ao: boolean;
  /** Fase 13: reflexo em tela e luz indireta, em meia resolução (só no Alto). */
  indirect: boolean;
  /** Fase 13: o sol na lente — raios (`rays`) e o reflexo (`flare`). */
  rays: boolean;
}

/**
 * O plano: Baixo, ou todas as opções desligadas, não tem compositor; o bloom no Médio (desde a F61: o
 * neon da malha é a linguagem do jogo, ADR 0014) e no Alto, com alvo em ponto flutuante (num alvo de 8
 * bits nada passa de 1, o limiar do bloom); no Médio, com menos níveis de borrão. A onda de choque (F94)
 * só no Alto, sempre que há compositor (parada, não muda a imagem).
 */
export function postPlan(tier: PostTier, toggles: PostToggles, caps: PostCaps): PostPlan {
  const on = tier !== "baixo";
  const bloom = on && toggles.bloom && caps.float;
  const grade = on && toggles.grade;
  const vignette = on && toggles.vignette;
  const levels = bloom ? (tier === "alto" ? POST_STYLE.levels : POST_STYLE.levelsMedio) : 0;
  // Fase 13 (G5): os de luz pedem alvo em meio-float (o HDR do neon é o que reflete e ilumina).
  const ao = on && toggles.light;
  const indirect = tier === "alto" && toggles.light && caps.float;
  const rays = on && toggles.light;
  const flare = on && toggles.flare;
  const aberration = on && toggles.aberration;
  const motion = on && toggles.motion;
  const composer = bloom || grade || vignette || ao || indirect || rays || flare || aberration || motion;
  return { tier, composer, bloom, grade, vignette, light: toggles.light, aberration, flare, motion, float: caps.float, levels, onda: composer && tier === "alto", ao, indirect, rays };
}

/** Os efeitos do passe de um plano, na ordem do shader (a cor sempre, quando há compositor). */
export function postEffects(plan: PostPlan): string[] {
  if (!plan.composer) return [];
  return [
    ...(plan.ao ? ["oclusao"] : []),
    ...(plan.indirect ? ["indireta"] : []),
    ...(plan.rays || plan.flare ? ["sol"] : []),
    ...(plan.onda ? ["onda"] : []),
    ...(plan.motion ? ["movimento"] : []),
    ...(plan.bloom ? ["bloom"] : []),
    "cor",
    ...(plan.vignette ? ["vinheta"] : []),
    ...(plan.aberration ? ["aberracao"] : []),
  ];
}

/** Dois planos pedem o mesmo compositor (o resto é uniform). */
export function samePlan(a: PostPlan, b: PostPlan): boolean {
  return a.composer === b.composer && a.bloom === b.bloom && a.grade === b.grade && a.vignette === b.vignette && a.float === b.float && a.levels === b.levels && a.onda === b.onda
    && a.ao === b.ao && a.indirect === b.indirect && a.rays === b.rays && a.flare === b.flare && a.aberration === b.aberration && a.motion === b.motion;
}

/** As opções de cada tier (as de Gráficos partem daqui e o jogador muda à mão). */
export const POST_PRESETS: Record<PostTier, PostToggles> = {
  baixo: { bloom: false, grade: false, vignette: false, light: false, aberration: false, flare: false, motion: false },
  // Fase 14 (decisão do dono): o desfoque de movimento ligado só no Alto; no Médio, opção.
  medio: { bloom: true, grade: true, vignette: true, light: true, aberration: true, flare: false, motion: false },
  alto: { bloom: true, grade: true, vignette: true, light: true, aberration: true, flare: true, motion: true },
};

/**
 * Estilo (decisões do dono, 2026-10-06). Bloom: limiar 1, suavidade 0,1, 5 níveis do borrão de
 * mipmaps com raio `bloomRadius` (o halo fica perto do emissivo), força `bloom` (`bloomReduced` com
 * "Reduzir lampejos"); no Alto, os emissivos sobem à luminância `glowLuminance` (`uGlowGain`). Correção de cor em sRGB: `light` clareia os meios-tons, `contrast`
 * puxa uma curva em S suave, `saturation` e `warmth` (vermelho +, azul −). Vinheta: a conta da do
 * pacote (`offset`, `darkness`), só nos cantos. Multisampling 4 até `msaaPixels` no alvo.
 */
export const POST_STYLE = {
  bloom: 0.5,
  bloomReduced: 0.22,
  threshold: 1,
  smoothing: 0.1,
  levels: 5,
  levelsMedio: 3,
  bloomRadius: 0.4,
  glowLuminance: 1.5,
  light: 0.05,
  contrast: 0.12,
  saturation: 0,
  warmth: 0,
  vignetteOffset: 0.5,
  vignetteDarkness: 0.2,
  msaaPixels: 2_500_000,
  // Fase 13 (G5, a referência "Mintlit"): forças do reflexo em tela, da luz indireta (e o raio, m), dos
  // raios do sol e do reflexo de lente; a aberração (deslocamento em uv na borda).
  ssr: 0.7,
  gi: 1.1,
  giRadius: 2.4,
  rays: 0.32,
  flare: 0.55,
  aberration: 0.0012,
} as const;

/**
 * Amostras do multisampling do compositor: 4 até ~2,5 MP no alvo (acima, o custo dobra sem ganho
 * visível), limitadas ao que o formato do alvo aceita (`supported`; abaixo de 2, nenhuma).
 */
export const msaaSamples = (width: number, height: number, supported = 4): number => (width * height <= POST_STYLE.msaaPixels && supported >= 2 ? Math.min(4, supported) : 0);

/**
 * Força do bloom: a do estilo, limitada com "Reduzir lampejos". `vignetteFade` (0 a 1) apaga a
 * vinheta enquanto uma das vinhetas CSS do jogo (vida baixa, afogando, o Cerco) está na tela: as
 * duas juntas escureceriam a borda duas vezes.
 */
export function postParams(settings: Pick<PostSettings, "reduceFlashes">, vignetteFade = 0): { bloom: number; vignette: number } {
  return { bloom: settings.reduceFlashes ? Math.min(POST_STYLE.bloom, POST_STYLE.bloomReduced) : POST_STYLE.bloom, vignette: POST_STYLE.vignetteDarkness * (1 - Math.min(1, Math.max(0, vignetteFade))) };
}

/**
 * Os materiais do pós (os programas dele, à parte dos da cena): os do pacote `postprocessing` que o plano
 * usa, os da oclusão (`SSAOEffect`) e, da Fase 13, os passes que leem a profundidade (`post-cinema.ts`).
 */
export const POST_MATERIALS: ReadonlySet<string> = new Set(["EffectMaterial", "LuminanceMaterial", "DownsamplingMaterial", "UpsamplingMaterial", "CopyMaterial", "SSAOMaterial", "DepthDownsamplingMaterial", "NormaisDaProfundidade", "LuzIndireta"]);

/** Programas compilados da cena e do pós (`renderer.info.programs`; o three dá ao programa o nome do material). */
export function countPrograms(programs: readonly { name: string }[] | null | undefined): { scene: number; post: number } {
  let post = 0;
  for (const p of programs ?? []) if (POST_MATERIALS.has(p.name)) post++;
  return { scene: (programs?.length ?? 0) - post, post };
}

// ---- espelho dos shaders (testes e e2e) ----

const srgb = (c: number): number => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
const linear = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

/** Parâmetros da correção de cor; com tudo 0, ela só limita a 1 (o que o canvas faria). */
export interface GradeParams {
  light: number;
  contrast: number;
  saturation: number;
  warmth: number;
}

export const GRADE_IDENTITY: GradeParams = { light: 0, contrast: 0, saturation: 0, warmth: 0 };
/**
 * A graduação com a cena dessaturada em `amount` (0 a 1; 1 = cinza): vida baixa e afogando (F75), no lugar
 * da vinheta vermelha. Só a saturação muda.
 */
export function desaturated(p: GradeParams, amount: number): GradeParams {
  const a = clamp01(amount);
  return a === 0 ? p : { ...p, saturation: p.saturation + (-1 - p.saturation) * a };
}

export const GRADE_STYLE: GradeParams = { light: POST_STYLE.light, contrast: POST_STYLE.contrast, saturation: POST_STYLE.saturation, warmth: POST_STYLE.warmth };

/**
 * A correção de cor do `GradeEffect`, em TypeScript: cor linear dentro e fora. Cada canal para em 1;
 * com o bloom (`hdr`), o que entra nele (a luminância acima do limiar, com a suavidade) é dividido
 * pelo maior canal (o matiz fica).
 */
export function gradeLinear(rgb: readonly [number, number, number], p: GradeParams, hdr = false): [number, number, number] {
  const c = rgb.map((v) => Math.max(0, v)) as [number, number, number];
  const peak = Math.max(...c);
  const t = clamp01((0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2] - POST_STYLE.threshold) / POST_STYLE.smoothing);
  const bloomed = hdr ? t * t * (3 - 2 * t) : 0;
  const limited = c.map((v) => Math.min(v, 1) + (v / Math.max(peak, 1) - Math.min(v, 1)) * bloomed);
  let s = limited.map((v) => srgb(v));
  s = s.map((v) => v ** (1 / (1 + p.light)));
  s = s.map((v) => v + (v * v * (3 - 2 * v) - v) * p.contrast);
  const l = 0.2126 * s[0]! + 0.7152 * s[1]! + 0.0722 * s[2]!;
  s = s.map((v) => l + (v - l) * (1 + p.saturation));
  const warm = [1 + p.warmth, 1 + p.warmth * 0.3, 1 - p.warmth];
  return s.map((v, i) => linear(clamp01(v * warm[i]!))) as [number, number, number];
}

/** O fator da vinheta (`VinhetaEffect`, a conta da técnica padrão do pacote) no ponto `uv` (0 a 1) da tela. */
export function vignetteFactor(u: number, v: number, offset: number = POST_STYLE.vignetteOffset, darkness: number = POST_STYLE.vignetteDarkness): number {
  const d = Math.hypot(u - 0.5, v - 0.5) * (darkness + offset);
  const e0 = 0.8;
  const e1 = offset * 0.799;
  const t = clamp01((d - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

/** sRGB 0–255 → linear (0 a 1). */
export const srgb8ToLinear = (v: number): number => linear(v / 255);
/** Linear → sRGB 0–255 (arredondado, como a sonda). */
export const linearToSrgb8 = (v: number): number => Math.round(srgb(clamp01(v)) * 255);

// ---- desfoque de movimento (Fase 14) ----

/**
 * Desfoque de movimento (Fase 14, a referência do golpe), sem buffer de velocidade: dois rastros de tela.
 * O da corrida (a Investida, o Salto do jogador local) borra na direção em que o corpo anda na tela, com
 * `streak` de comprimento (fração da altura da tela) na força 1, e deixa o corpo nítido num círculo de
 * `mask[0]` a `mask[1]` (fração da altura) em volta dele. O do golpe forte (crítico, abate) abre em zoom a
 * partir do ponto do impacto, `zoom` na força 1, e some ao quadrado em `zoomMs` ms. Parado (nada pedido),
 * os números são 0 e o efeito devolve a entrada.
 */
export const MOTION = { streak: 0.018, zoom: 0.06, zoomMs: 170, mask: [0.09, 0.28] as const, taps: 8, full: 0.05 } as const;

/** Valores por quadro: o rastro (direção u, v; centro u, v) e o zoom (centro u, v; força; 0). */
export const MOTION_FLOATS = 8;

/** O desfoque pode valer? Só com o efeito no compositor e sem "Reduzir lampejos". */
export function motionAllowed(plan: PostPlan | undefined, settings: Pick<PostSettings, "reduceFlashes">): boolean {
  return plan?.motion === true && !settings.reduceFlashes;
}

/**
 * O estado do desfoque, puro para os testes: o rastro vale só no quadro em que foi pedido (a cena pede a
 * cada quadro enquanto o corpo corre); o zoom, o último pedido, pelo tempo dele.
 */
export class MotionBlur {
  private readonly streak = [0, 0, 0.5, 0.5];
  private streaking = false;
  private zoom: { u: number; v: number; strength: number; start: number } | undefined;

  /**
   * O corpo em (u, v) na tela anda (du, dv) num décimo de segundo, medido na altura da tela (o x já vezes `aspect`),
   * com a força 0 a 1. O passo sai em uv (o x dividido de volta pelo `aspect`).
   */
  setStreak(u: number, v: number, du: number, dv: number, strength: number, aspect = 1): void {
    const len = Math.hypot(du, dv);
    if (len < 1e-6 || strength <= 0) return;
    // O comprimento cresce com a velocidade na tela (`full`: o corpo atravessando a tela de lado); de
    // frente ou de costas para a câmera, quase nada se move na tela e o borrão fica curto.
    const k = (MOTION.streak * Math.min(1, strength, len / MOTION.full)) / len;
    this.streak[0] = (du * k) / aspect;
    this.streak[1] = dv * k;
    this.streak[2] = u;
    this.streak[3] = v;
    this.streaking = true;
  }

  /** O zoom do golpe forte em (u, v), força 0 a 1, a partir de `now` (ms). */
  impact(u: number, v: number, strength: number, now: number): void {
    if (strength <= 0) return;
    this.zoom = { u, v, strength: Math.min(1, strength), start: now };
  }

  /** O zoom ainda vale em `now`. */
  zooming(now: number): boolean {
    return this.zoom !== undefined && now - this.zoom.start < MOTION.zoomMs;
  }

  /** Escreve o quadro em `out` (`MOTION_FLOATS`) e esquece o rastro deste quadro. */
  write(now: number, out: Float32Array): Float32Array {
    out.fill(0);
    if (this.streaking) out.set(this.streak, 0);
    this.streaking = false;
    const z = this.zoom;
    if (z) {
      const k = (now - z.start) / MOTION.zoomMs;
      if (k >= 1) this.zoom = undefined;
      else if (k >= 0) {
        out[4] = z.u;
        out[5] = z.v;
        out[6] = MOTION.zoom * z.strength * (1 - k) * (1 - k);
      }
    }
    return out;
  }

  clear(): void {
    this.streaking = false;
    this.zoom = undefined;
  }
}

/**
 * Espelho do `MovimentoEffect` (testes): de quanto (uv) é o passo da média no pixel (u, v), o rastro pela
 * máscara em volta do corpo mais o zoom pela distância ao centro dele. `aspect` = largura / altura.
 */
export function motionStep(data: ArrayLike<number>, u: number, v: number, aspect: number): [number, number] {
  const d = Math.hypot((u - data[2]!) * aspect, v - data[3]!);
  const t = Math.min(1, Math.max(0, (d - MOTION.mask[0]) / (MOTION.mask[1] - MOTION.mask[0])));
  const keep = t * t * (3 - 2 * t);
  return [data[0]! * keep + (u - data[4]!) * data[6]!, data[1]! * keep + (v - data[5]!) * data[6]!];
}

// ---- onda de choque (F94) ----

/**
 * Onda de choque (F94, só no Alto): um anel que abre na tela em volta de um impacto e desloca a imagem
 * para fora. Até `max` ao mesmo tempo, cada uma `ms` ms. O raio final é em metros (o do golpe) e vira
 * fração da altura da tela pela profundidade do centro. A amplitude (fração da altura da tela, com força
 * 1) cai com o tempo e, pela distância até a câmera, de cheia em `near` m a nada em `far` m. A espessura
 * do anel é `width` do raio final na tela, entre `minWidth` e `maxWidth`, e o deslocamento nunca passa
 * de `fold` da espessura (a imagem não dobra), nem somado, onde dois anéis se cruzam. Duas ondas a menos de
 * `gap` ms uma da outra seriam quase a mesma: fica a mais forte.
 */
export const SHOCK = {
  max: 2,
  ms: 600,
  /** Fração da vida em que a amplitude sobe (depois cai ao quadrado). */
  attack: 0.08,
  amplitude: 0.03,
  width: 0.35,
  minWidth: 0.02,
  maxWidth: 0.12,
  fold: 0.4,
  gap: 150,
  near: 12,
  far: 45,
  radius: 5,
  /** Centro a menos disto à frente da câmera (m, o `w` do recorte): atrás dela, sem onda. */
  minDepth: 0.2,
} as const;

/** Valores por quadro: centro (u, v), raio e amplitude de cada onda, e as duas espessuras no fim. */
export const SHOCK_FLOATS = SHOCK.max * 4 + SHOCK.max;

/** A onda pode nascer? Só com o compositor do Alto vivo e sem "Reduzir lampejos". */
export function shockAllowed(plan: PostPlan | undefined, settings: Pick<PostSettings, "reduceFlashes">): boolean {
  return plan?.onda === true && !settings.reduceFlashes;
}

/** O raio na fração `k` da vida (fração do final): abre rápido e freia. Fora da vida, 0. */
export function shockRadius(k: number): number {
  if (k < 0 || k >= 1) return 0;
  const rest = 1 - k;
  return 1 - rest * rest * rest;
}

/** A amplitude na fração `k` da vida (fração da cheia): sobe em `attack` e cai ao quadrado. Fora da vida, 0. */
export function shockAmplitude(k: number): number {
  if (k <= 0 || k >= 1) return 0;
  return Math.min(1, k / SHOCK.attack) * (1 - k) * (1 - k);
}

/** Amplitude pela distância do impacto até a câmera (m): cheia até `near`, nada a partir de `far`. */
export function shockFalloff(distance: number): number {
  const t = clamp01((distance - SHOCK.near) / (SHOCK.far - SHOCK.near));
  return 1 - t * t * (3 - 2 * t);
}

/** A câmera do quadro: `viewProj` (projeção × vista, colunas como no three), `focal` (o elemento [5] da projeção) e a posição. */
export interface ShockCamera {
  viewProj: ArrayLike<number>;
  focal: number;
  x: number;
  y: number;
  z: number;
}

/**
 * O ponto (x, y, z) na tela: `u`, `v` de 0 a 1 (a conta do `Vector3.project`) e o `w` do recorte (a
 * profundidade à frente da câmera; ≤ 0 atrás dela). Escreve em `out`, sem alocar.
 */
export function projectPoint(m: ArrayLike<number>, x: number, y: number, z: number, out: { u: number; v: number; w: number }): typeof out {
  const w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
  out.w = w;
  out.u = ((m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w) * 0.5 + 0.5;
  out.v = ((m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w) * 0.5 + 0.5;
  return out;
}

/**
 * Quanto um anel desloca o pixel em (u, v), em fração da altura da tela, para fora do centro (o `ring` do
 * `OndaEffect`). `wave` = centro (u, v), raio e amplitude; `aspect` = largura / altura.
 */
export function shockOffset(wave: readonly [number, number, number, number], width: number, u: number, v: number, aspect: number): number {
  if (wave[3] <= 0) return 0;
  const len = Math.hypot((u - wave[0]) * aspect, v - wave[1]);
  const x = (len - wave[2]) / width;
  if (Math.abs(x) >= 1 || len <= 0) return 0;
  const s = 1 - x * x;
  return wave[3] * s * s;
}

/**
 * O deslocamento do pixel em (u, v) pelas duas ondas (o espelho do `OndaEffect`), em uv, escrito em `out`: a soma
 * dos anéis, com o comprimento limitado a `fold` da maior espessura entre os que deslocam, e encolhido perto da
 * borda da tela para a leitura não sair dela (o pixel lê de `uv − deslocamento`). `data`: o quadro do `write`.
 */
export function shockDisplacement(data: ArrayLike<number>, u: number, v: number, aspect: number, out: { x: number; y: number }): typeof out {
  out.x = 0;
  out.y = 0;
  let widest = 0;
  for (let i = 0; i < SHOCK.max; i++) {
    const cu = data[i * 4]!;
    const cv = data[i * 4 + 1]!;
    const width = data[SHOCK.max * 4 + i]!;
    const s = shockOffset([cu, cv, data[i * 4 + 2]!, data[i * 4 + 3]!], width, u, v, aspect);
    if (s <= 0) continue;
    const dx = (u - cu) * aspect;
    const dy = v - cv;
    const len = Math.hypot(dx, dy);
    out.x += (dx / len) * s / aspect;
    out.y += (dy / len) * s;
    widest = Math.max(widest, width);
  }
  if (widest === 0) return out;
  const len = Math.hypot(out.x * aspect, out.y);
  const cap = SHOCK.fold * widest;
  if (len > cap) {
    out.x *= cap / len;
    out.y *= cap / len;
  }
  const edge = Math.min(u, 1 - u, v, 1 - v);
  const reach = Math.max(Math.abs(out.x), Math.abs(out.y));
  if (reach > edge) {
    const k = Math.max(0, edge) / reach;
    out.x *= k;
    out.y *= k;
  }
  return out;
}

/**
 * As ondas vivas (F94), em vagas fixas: nascer, andar e sumir sem alocar. Com as `max` vagas ocupadas, a
 * nova toma a que tem menos força restante; a menos de `gap` ms de outra, só a mais forte fica. `write` escreve o quadro em `SHOCK_FLOATS` números: a onda
 * que não começou, já acabou, está atrás da câmera ou longe demais sai com amplitude 0.
 */
export class Shockwaves {
  /** Por vaga: x, y, z, força, raio final (m) e o início (ms). */
  private readonly slots = new Float64Array(SHOCK.max * 6);
  private readonly point = { u: 0, v: 0, w: 0 };

  constructor() {
    this.clear();
  }

  /** Ondas que ainda não acabaram em `now` (as marcadas para depois contam). */
  alive(now: number): number {
    let n = 0;
    for (let i = 0; i < SHOCK.max; i++) if (now < (this.slots[i * 6 + 5] as number) + SHOCK.ms) n++;
    return n;
  }

  /** Uma onda em (x, y, z) a partir de `now` (ms; pode ser no futuro), com a força (0 a 1) e o raio final (m). */
  spawn(x: number, y: number, z: number, strength: number, radius: number, now: number): boolean {
    if (!(strength > 0) || !(radius > 0)) return false;
    const twin = this.twin(now);
    if (twin >= 0 && strength <= (this.slots[twin * 6 + 3] as number)) return false;
    const pick = twin >= 0 ? twin : this.weakest(now);
    const o = pick * 6;
    this.slots[o] = x;
    this.slots[o + 1] = y;
    this.slots[o + 2] = z;
    this.slots[o + 3] = Math.min(1, strength);
    this.slots[o + 4] = radius;
    this.slots[o + 5] = now;
    return true;
  }

  /** A viva que começa a menos de `gap` ms de `now` (quase a mesma onda), ou −1. */
  private twin(now: number): number {
    for (let i = 0; i < SHOCK.max; i++) {
      const start = this.slots[i * 6 + 5] as number;
      if (now < start + SHOCK.ms && Math.abs(now - start) < SHOCK.gap) return i;
    }
    return -1;
  }

  /** Uma vaga livre (acabou), ou a que tem menos força pela frente (a marcada para depois, a força inteira). */
  private weakest(now: number): number {
    let pick = 0;
    let weakest = Infinity;
    for (let i = 0; i < SHOCK.max; i++) {
      const k = (now - (this.slots[i * 6 + 5] as number)) / SHOCK.ms;
      const left = k >= 1 ? -1 : (this.slots[i * 6 + 3] as number) * (k < 0 ? 1 : (1 - k) * (1 - k));
      if (left < weakest) {
        weakest = left;
        pick = i;
      }
    }
    return pick;
  }

  /** O quadro em `out` (`SHOCK_FLOATS` números). Devolve quantas ondas desenham. */
  write(now: number, camera: ShockCamera, out: Float32Array): number {
    let drawn = 0;
    for (let i = 0; i < SHOCK.max; i++) {
      const o = i * 6;
      const base = i * 4;
      out[base] = 0;
      out[base + 1] = 0;
      out[base + 2] = 0;
      out[base + 3] = 0;
      out[SHOCK.max * 4 + i] = SHOCK.minWidth;
      const k = (now - (this.slots[o + 5] as number)) / SHOCK.ms;
      const amplitude = shockAmplitude(k);
      if (amplitude <= 0) continue;
      const x = this.slots[o] as number;
      const y = this.slots[o + 1] as number;
      const z = this.slots[o + 2] as number;
      const p = projectPoint(camera.viewProj, x, y, z, this.point);
      const fall = shockFalloff(Math.hypot(x - camera.x, y - camera.y, z - camera.z));
      if (p.w < SHOCK.minDepth || fall <= 0) continue;
      // Metros → fração da altura da tela na profundidade do centro.
      const scale = camera.focal / (2 * p.w);
      const final = (this.slots[o + 4] as number) * scale;
      const width = Math.min(SHOCK.maxWidth, Math.max(SHOCK.minWidth, final * SHOCK.width));
      out[base] = p.u;
      out[base + 1] = p.v;
      out[base + 2] = final * shockRadius(k);
      out[base + 3] = Math.min(width * SHOCK.fold, SHOCK.amplitude * (this.slots[o + 3] as number) * amplitude * fall);
      out[SHOCK.max * 4 + i] = width;
      drawn++;
    }
    return drawn;
  }

  clear(): void {
    for (let i = 0; i < SHOCK.max; i++) {
      this.slots.fill(0, i * 6, i * 6 + 5);
      this.slots[i * 6 + 5] = -Infinity;
    }
  }
}
